import React, { useState, useEffect, useRef } from "react";
import Carousel from "react-bootstrap/Carousel";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import AboutImg from "../assets/images/about.png";
import CustomCarousel from "../Components/CustomCarousel";
import ServiceCard from "../Components/ServiceCard";
import EventCard from "../Components/EventCard";
import { ArrowLeft, ArrowRight } from "react-bootstrap-icons";
import { Helmet } from "react-helmet-async";
import {
  poojaImages,
  eventImages,
  images,
  heroContent,
  aboutContent,
  specialPoojasContent,
  upcomingEventsContent,
  visualJourneyContent,
  carouselSettings,
} from "../Constants/data.jsx";
import { usePoojas } from "../hooks/usePoojas";
import { useServices } from "../hooks/useServices";
import { useEvents } from "../hooks/useEvents";
import HeroVideo from "../assets/videos/video.mp4";

const Home = ({
  items = poojaImages,
  interval = carouselSettings.defaultInterval,
}) => {
  const navigate = useNavigate();

  // Add this function near your other handler functions
  const handleEventClick = (eventSlug) => {
    navigate(`/event/${eventSlug}`);
  };

  // Use the hooks - API ONLY
  const { poojas } = usePoojas();
  const { services } = useServices();
  const { getDetailedEvents } = useEvents();
  const apiEvents = getDetailedEvents();

  const carouselRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const handleScroll = (direction) => {
    if (!carouselRef.current) return;
    if (direction === "left") {
      carouselRef.current.scrollBy({ left: -300, behavior: "smooth" });
    } else {
      carouselRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  // Check if device mobile (shared for Hero + Temple Services)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==================== TEMPLE SERVICES STATE / LOGIC ====================
  const [serviceCurrentIndex, setServiceCurrentIndex] = useState(0);

  const servicesRowRef = useRef(null);
  const [serviceTouchStart, setServiceTouchStart] = useState(null);
  const [serviceTouchEnd, setServiceTouchEnd] = useState(null);

  // UPDATE: Use slug instead of ID
  const handleServiceClick = (serviceSlug) => {
    navigate(`/service/${serviceSlug}`);
  };

  // Update the handleServiceNext and handleServicePrev functions:
  const handleServiceNext = () => {
    if (services.length === 0) return;

    if (isMobile) {
      setServiceCurrentIndex((i) => (i + 1) % services.length);
    } else if (services.length > 3) {
      // For desktop carousel with more than 3 cards
      setServiceCurrentIndex((i) => (i + 1) % services.length);
    }
  };

  const handleServicePrev = () => {
    if (services.length === 0) return;

    if (isMobile) {
      setServiceCurrentIndex(
        (i) => (i - 1 + services.length) % services.length,
      );
    } else if (services.length > 3) {
      // For desktop carousel with more than 3 cards
      setServiceCurrentIndex(
        (i) => (i - 1 + services.length) % services.length,
      );
    }
  };

  // Swipe controls for Temple Services
  const onServiceTouchStart = (e) => {
    setServiceTouchEnd(null);
    setServiceTouchStart(e.touches[0].clientX);
  };
  const onServiceTouchMove = (e) => setServiceTouchEnd(e.touches[0].clientX);
  const onServiceTouchEnd = () => {
    if (!serviceTouchStart || !serviceTouchEnd) return;
    if (serviceTouchStart - serviceTouchEnd > 50) handleServiceNext();
    if (serviceTouchStart - serviceTouchEnd < -50) handleServicePrev();
  };

  // ==================== SPECIAL POOJAS ====================
  const [activeIndex, setActiveIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);

  const updateSlidesToShow = () => {
    const width = window.innerWidth;
    if (width >= 1024) {
      setSlidesToShow(3);
    } else if (width >= 768) {
      setSlidesToShow(2);
    } else {
      setSlidesToShow(1);
    }
  };

  useEffect(() => {
    updateSlidesToShow();
    window.addEventListener("resize", updateSlidesToShow);

    return () => window.removeEventListener("resize", updateSlidesToShow);
  }, []);

  useEffect(() => {
    if (poojas.length > 0) {
      const slideInterval = setInterval(() => {
        handleNext();
      }, interval);

      return () => clearInterval(slideInterval);
    }
  }, [activeIndex, interval, poojas.length]);

  const handleNext = () => {
    if (poojas.length > 0) {
      setActiveIndex((prevIndex) => (prevIndex + 1) % poojas.length);
    }
  };

  const handlePrev = () => {
    if (poojas.length > 0) {
      setActiveIndex(
        (prevIndex) => (prevIndex - 1 + poojas.length) % poojas.length,
      );
    }
  };

  // Touch scroll functionality for special poojas
  const [poojatouchStart, setPoojatouchStart] = useState(null);
  const [poojatouchEnd, setPoojatouchEnd] = useState(null);

  const onPoojaTouchStart = (e) => {
    setPoojatouchEnd(null);
    setPoojatouchStart(e.targetTouches[0].clientX);
  };

  const onPoojaTouchMove = (e) => {
    setPoojatouchEnd(e.targetTouches[0].clientX);
  };

  const onPoojaTouchEnd = () => {
    if (!poojatouchStart || !poojatouchEnd) return;

    const distance = poojatouchStart - poojatouchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  const getCurrentPoojas = () => {
    if (poojas.length === 0) return [];

    const currentItems = [];
    for (let i = 0; i < slidesToShow; i++) {
      currentItems.push(poojas[(activeIndex + i) % poojas.length]);
    }
    return currentItems;
  };

  //   const getCurrentPoojas = () => {
  //   if (poojas.length === 0) return [];

  //   const currentItems = [];
  //   const itemsToShow = Math.min(slidesToShow, poojas.length);

  //   for (let i = 0; i < itemsToShow; i++) {
  //     currentItems.push(poojas[(activeIndex + i) % poojas.length]);
  //   }
  //   return currentItems;
  // };

  // ==================== UPCOMING EVENTS ====================
  const [visibleEventsCount, setVisibleEventsCount] = useState(4);
  const [eventStartIndex, setEventStartIndex] = useState(0);

  // Safe date parser for Home page
  const parseEventDate = (dateString) => {
    if (!dateString) return null;

    try {
      const value = String(dateString).trim();

      // Handle ISO format with timezone
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})T/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return new Date(
          Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0),
        );
      }

      // Handle YYYY-MM-DD
      const simpleIsoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (simpleIsoMatch) {
        const [, year, month, day] = simpleIsoMatch;
        return new Date(
          Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0),
        );
      }

      // Fallback
      const date = new Date(value);
      if (isNaN(date.getTime())) return null;

      return date;
    } catch (error) {
      console.error("Error parsing event date:", error);
      return null;
    }
  };

  // Today's date in UTC
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  // Use endDate to decide whether event is upcoming
  // fallback to startDate if endDate is missing
  const upcomingEvents = apiEvents.filter((event) => {
    const compareDate = parseEventDate(event.endDate || event.startDate);

    if (!compareDate || isNaN(compareDate.getTime())) return false;

    compareDate.setUTCHours(0, 0, 0, 0);

    return compareDate >= today;
  });

  // Sort upcoming events by startDate first, fallback to endDate
  const sortedUpcomingEvents = [...upcomingEvents].sort((a, b) => {
    const dateA = parseEventDate(a.startDate || a.endDate);
    const dateB = parseEventDate(b.startDate || b.endDate);

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return dateA - dateB;
  });

  // Update the visible events count based on screen size
  const updateVisibleEventsCount = () => {
    const width = window.innerWidth;
    let desired = 1;

    if (width >= 1200)
      desired = 4; // xl+
    else if (width >= 992)
      desired = 3; // lg
    else if (width >= 768)
      desired = 2; // md
    else desired = 1; // sm/xs

    setVisibleEventsCount(Math.min(desired, sortedUpcomingEvents.length));
  };

  useEffect(() => {
    updateVisibleEventsCount();
    window.addEventListener("resize", updateVisibleEventsCount);
    return () => window.removeEventListener("resize", updateVisibleEventsCount);
  }, [sortedUpcomingEvents.length]);

  const handleEventsNext = () => {
    if (sortedUpcomingEvents.length > 0) {
      setEventStartIndex((prev) => (prev + 1) % sortedUpcomingEvents.length);
    }
  };

  const handleEventsPrev = () => {
    if (sortedUpcomingEvents.length > 0) {
      setEventStartIndex(
        (prev) =>
          (prev - 1 + sortedUpcomingEvents.length) %
          sortedUpcomingEvents.length,
      );
    }
  };

  // Touch scroll functionality for upcoming events
  const [eventTouchStart, setEventTouchStart] = useState(null);
  const [eventTouchEnd, setEventTouchEnd] = useState(null);

  const onEventTouchStart = (e) => {
    setEventTouchEnd(null);
    setEventTouchStart(e.targetTouches[0].clientX);
  };

  const onEventTouchMove = (e) => {
    setEventTouchEnd(e.targetTouches[0].clientX);
  };

  const onEventTouchEnd = () => {
    if (!eventTouchStart || !eventTouchEnd) return;

    const distance = eventTouchStart - eventTouchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleEventsNext();
    } else if (isRightSwipe) {
      handleEventsPrev();
    }
  };

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />

        {/* Open Graph */}
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        {/* Social Links */}
        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Facebook  */}

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Instagram */}
        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />

        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>

      <div className="container-fluid p-0 ">
        {/* Hero Section */}
        <section className="position-relative text-white d-flex align-items-center justify-content-center overflow-hidden text-center py-5 vh-100">
          {/* Video Background */}
          <div className="video-background position-absolute w-100 h-100 z-0">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-100 h-100 object-fit-cover"
              poster="../assets/images/video-poster.jpg"
            >
              <source src={HeroVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {/* Overlay for better readability */}
            <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50"></div>
          </div>

          <motion.div
            className="container d-flex flex-column justify-content-center align-items-center position-relative z-3 hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="col-md-8 text-center flex-column">
              <motion.h1
                className="display-3 fw-bold mb-3 overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
              >
                {heroContent.title}
              </motion.h1>

              <motion.p
                className="lead mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                {heroContent.description}
              </motion.p>

              <div className="d-flex justify-content-center gap-3 mt-5">
                <a
                  href="/contactus"
                  className="btn btn-warning px-4 py-3 hover-button text-white fw-bold border-0 position-relative rounded-5"
                >
                  Contact Us
                </a>
                <a
                  href="/about"
                  className="btn btn-warning px-4 py-3 hover-button text-white fw-bold border-0 position-relative rounded-5"
                >
                  About Us
                </a>
              </div>
            </div>
          </motion.div>
        </section>

        {/* About Section */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="bg-white py-5">
              <div className="container">
                <div className="row d-flex align-items-center">
                  <motion.div
                    className="col-md-6"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    <div className="image-container">
                      <img
                        src={AboutImg}
                        alt="Ayyappa Swami Temple"
                        className="img-fluid rounded about-image d-flex align-items-center"
                        style={{ minHeight: "400px" }}
                      />
                    </div>
                  </motion.div>
                  <motion.div
                    className="col-md-6 d-flex flex-column text-center text-md-start justify-content-between"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    <div>
                      <motion.h4
                        className="heading flex-grow-1 mb-3 mt-5 mt-md-0 fw-bold"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.2,
                          ease: "easeOut",
                        }}
                      >
                        {aboutContent.title}
                      </motion.h4>
                      <motion.h2
                        className="about-heading flex-grow-1 mb-3 px-2 p-md-0"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.2,
                          ease: "easeOut",
                        }}
                      >
                        {aboutContent.heading}
                      </motion.h2>
                      <motion.p
                        className="about-description flex-grow-1 mb-4 fs-5 lh-md px-4 p-md-0"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "easeOut",
                        }}
                      >
                        {aboutContent.description}
                      </motion.p>
                      <motion.h3
                        className="schedule-title flex-grow-1 mb-3"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.6,
                          ease: "easeOut",
                        }}
                      >
                        {aboutContent.scheduleTitle}
                      </motion.h3>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                      >
                        <div className="row">
                          <div className="col-xs-12 col-md-6 mb-3">
                            <div>
                              <h4 className="heading mb-2">
                                {aboutContent.weekdays.title}
                              </h4>
                              <p className="schedule-time mb-1">
                                {aboutContent.weekdays.morning}
                              </p>
                              <p className="schedule-time">
                                {aboutContent.weekdays.evening}
                              </p>
                            </div>
                          </div>
                          <div className="col-xs-12 col-md-6 mb-3">
                            <div>
                              <h4 className="heading mb-2 text-nowrap">
                                {aboutContent.weekends.title}
                              </h4>
                              <p className="schedule-time mb-1">
                                {aboutContent.weekends.morning}
                              </p>
                              <p className="schedule-time text">
                                {aboutContent.weekends.evening}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* ==================== TEMPLE SERVICES (API VERSION) ==================== */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="container-fluid text-center py-5"
              style={{ background: "#fce4e4" }}
            >
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-12">
                    {/* HEADINGS */}
                    <motion.h4
                      className="text-uppercase fw-bold heading"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      Enhancing Lives with Spiritual Guidance
                    </motion.h4>

                    <motion.h1
                      className="my-3 fw-bold service-title"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      Connecting Devotees Through Spiritual Temple Services
                    </motion.h1>

                    {/* WRAPPER */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="position-relative">
                        {/* DESKTOP LOGIC: Show grid for 3 or fewer cards, carousel for more */}
                        {!isMobile && services.length <= 3 ? (
                          /* ===================== DESKTOP GRID (3 or fewer cards) ===================== */
                          <div className="row justify-content-center align-items-stretch g-4">
                            {services.map((service, index) => (
                              <motion.div
                                key={service.id}
                                className="col-md-4 d-flex"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                              >
                                <ServiceCard
                                  service={service}
                                  onClick={() =>
                                    handleServiceClick(service.slug)
                                  }
                                  className="w-100 h-100"
                                />
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          /* ===================== CAROUSEL (Mobile OR Desktop with more than 3 cards) ===================== */
                          <div className="position-relative">
                            {/* Carousel Container */}
                            <div
                              className="d-flex justify-content-center w-100"
                              ref={servicesRowRef}
                              onTouchStart={onServiceTouchStart}
                              onTouchMove={onServiceTouchMove}
                              onTouchEnd={onServiceTouchEnd}
                              // style={{
                              //   touchAction: "pan-y",
                              //   minHeight: "450px",
                              // }}
                            >
                              <motion.div
                                key={serviceCurrentIndex}
                                className={`d-flex ${
                                  !isMobile
                                    ? "justify-content-center gap-4"
                                    : "justify-content-center"
                                } w-100`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.25 }}
                              >
                                {/* For desktop carousel with more than 3 cards, show 3 cards at once */}
                                {!isMobile && services.length > 3 ? (
                                  <div className="d-flex justify-content-center gap-4 w-100">
                                    {/* Show up to 3 cards in desktop carousel */}
                                    {[0, 1, 2].map((offset) => {
                                      const index =
                                        (serviceCurrentIndex + offset) %
                                        services.length;
                                      const service = services[index];
                                      return (
                                        <div
                                          key={`desktop-${service.id}-${offset}`}
                                          style={{ width: "350px" }}
                                        >
                                          <ServiceCard
                                            service={service}
                                            onClick={() =>
                                              handleServiceClick(service.slug)
                                            }
                                            style={{
                                              width: "100%",
                                              height: "420px",
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  /* Mobile carousel - single card */
                                  <ServiceCard
                                    service={services[serviceCurrentIndex]}
                                    onClick={() =>
                                      handleServiceClick(
                                        services[serviceCurrentIndex].slug,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      maxWidth: "420px",
                                      height: "420px",
                                    }}
                                  />
                                )}
                              </motion.div>
                            </div>

                            {/* Navigation Arrows - Show when needed */}
                            {services.length > 1 && (
                              <motion.div
                                className={`d-flex justify-content-center gap-3 ${
                                  !isMobile && services.length > 3
                                    ? "mt-4"
                                    : "mt-3"
                                }`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                              >
                                <div
                                  className="custom-border d-flex align-items-center justify-content-center rounded-circle cursor-pointer p-2"
                                  onClick={handleServicePrev}
                                >
                                  <ArrowLeft className="fs-5 heading" />
                                </div>
                                <div
                                  className="custom-border d-flex align-items-center justify-content-center rounded-circle cursor-pointer p-2"
                                  onClick={handleServiceNext}
                                >
                                  <ArrowRight className="fs-5 heading" />
                                </div>
                              </motion.div>
                            )}
                          </div>
                        )}

                        {/* No Services Available */}
                        {services.length === 0 && (
                          <div className="text-center py-5">
                            <p className="text-muted">
                              Our pooja services are being prepared with
                              devotion. Please stay tuned.
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Special Poojas Section - API ONLY */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="py-5 bg-white">
              <div className="container-fluid">
                <div className="row align-items-center ">
                  <motion.div
                    className="col-md-6 mb-4 mb-md-0 d-flex flex-column align-items-center align-items-md-end justify-content-end"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    <div className="d-flex flex-column align-items-center align-items-md-start justify-content-start col-12 col-md-10">
                      <motion.h4
                        className="heading text-center text-md-start fw-bold heading text-uppercase mb-3"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "easeOut",
                        }}
                      >
                        {specialPoojasContent.subtitle}
                      </motion.h4>
                      <motion.h2
                        className="mb-4 fw-bold text-start"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.6,
                          ease: "easeOut",
                        }}
                      >
                        {specialPoojasContent.title}
                      </motion.h2>

                      <motion.p
                        className="mb-3 text-muted text-center text-md-start fs-5 px-4 p-md-0"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                      >
                        {specialPoojasContent.description1}
                      </motion.p>
                      <motion.p
                        className="mb-4 text-muted text-center text-md-start fs-5 px-4 p-md-0"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 1.0,
                          ease: "easeOut",
                        }}
                      >
                        {specialPoojasContent.description2}
                      </motion.p>
                    </div>
                  </motion.div>

                  <motion.div
                    className="col-md-6 p-0"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                  >
                    {/* Show content when have data */}
                    {poojas.length > 0 && (
                      <Carousel
                        indicators={false}
                        controls={false}
                        interval={null}
                        className="mt-4"
                      >
                        <Carousel.Item>
                          <div
                            className="d-flex justify-content-center justify-content-lg-start gap-5"
                            onTouchStart={onPoojaTouchStart}
                            onTouchMove={onPoojaTouchMove}
                            onTouchEnd={onPoojaTouchEnd}
                            style={{ touchAction: "pan-y" }}
                          >
                            {getCurrentPoojas().map((item, index) => (
                              <motion.div
                                key={item.id}
                                className="d-flex flex-column align-items-start position-relative"
                                transition={{ duration: 0.3, ease: "easeOut" }}
                              >
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  className="rounded-3 carousel-image"
                                  style={{
                                    objectFit: "cover",
                                  }}
                                />
                                <div className="carousel-overlay p-3 bg-white position-absolute text-center rounded-3">
                                  {/* <p className="fs-6 fw-bold ">
                                    {item.schedule}
                                  </p> */}
                                  <h5 className="fs-6 fw-bold text-dark">
                                    {item.schedule}
                                  </h5>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </Carousel.Item>
                      </Carousel>
                    )}

                    {/* No Data State */}
                    {poojas.length === 0 && (
                      <div className="text-center py-5">
                        <p className="text-muted">
                          Our pooja services are currently being prepared with
                          devotion. Please check back soon.
                        </p>
                      </div>
                    )}
                  </motion.div>

                  {/* Arrows - Only show if we have poojas */}
                  {poojas.length > 0 && (
                    <motion.div
                      className="d-flex align-items-center justify-content-center gap-2 mt-5"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: 1.2,
                        ease: "easeOut",
                      }}
                    >
                      <motion.div
                        className="custom-border d-flex align-items-center justify-content-center rounded-circle"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ArrowLeft
                          onClick={handlePrev}
                          className="cursor-pointer fs-4 heading"
                        />
                      </motion.div>
                      <motion.div
                        className="custom-border d-flex align-items-center justify-content-center rounded-circle"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ArrowRight
                          onClick={handleNext}
                          className="cursor-pointer fs-4 heading"
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Upcoming Events Section - API ONLY (With Images) */}
        <section className="upcoming-events">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="container">
              <div className="row justify-content-center align-items-center">
                <div className="col-12 text-center py-5">
                  <motion.h4
                    className="text-uppercase heading fw-bold mb-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  >
                    {upcomingEventsContent.subtitle}
                  </motion.h4>

                  <motion.h2
                    className="mb-5 fw-bold"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                  >
                    {upcomingEventsContent.title}
                  </motion.h2>

                  {sortedUpcomingEvents.length > 0 && (
                    <>
                      <div className="position-relative">
                        <div className="row justify-content-center align-items-stretch g-4">
                          <div
                            className="col-12"
                            onTouchStart={onEventTouchStart}
                            onTouchMove={onEventTouchMove}
                            onTouchEnd={onEventTouchEnd}
                          >
                            <div
                              className="row justify-content-center align-items-center g-3"
                              onTouchStart={onEventTouchStart}
                              onTouchMove={onEventTouchMove}
                              onTouchEnd={onEventTouchEnd}
                            >
                              {Array.from({
                                length: Math.min(
                                  visibleEventsCount,
                                  sortedUpcomingEvents.length,
                                ),
                              }).map((_, i) => {
                                const currentVisibleCount = Math.min(
                                  visibleEventsCount,
                                  sortedUpcomingEvents.length,
                                );

                                const event =
                                  sortedUpcomingEvents[
                                    (eventStartIndex + i) %
                                      sortedUpcomingEvents.length
                                  ];

                                let colClass =
                                  "col-sm-12 col-md-6 mb-4 d-flex justify-content-center";

                                if (currentVisibleCount === 1) {
                                  colClass =
                                    "col-12 px-0 mb-4 d-flex justify-content-center";
                                } else if (currentVisibleCount === 2) {
                                  colClass =
                                    "col-lg-4 col-md-6 col-sm-12 mb-4 d-flex justify-content-center";
                                } else if (currentVisibleCount === 3) {
                                  colClass =
                                    "col-lg-4 col-md-6 col-sm-12 mb-4 d-flex justify-content-center";
                                } else if (currentVisibleCount === 4) {
                                  colClass =
                                    "col-lg-3 col-md-6 col-sm-12 mb-4 d-flex justify-content-center";
                                }

                                return (
                                  <div
                                    key={`${event.id}-${i}`}
                                    className={colClass}
                                  >
                                    <motion.div
                                      className="cursor-pointer w-100 mx-auto"
                                      whileHover={{ scale: 1.05 }}
                                      transition={{ duration: 0.2 }}
                                      onClick={() =>
                                        handleEventClick(event.slug)
                                      }
                                      style={{
                                        cursor: "pointer",
                                        width: "100%",
                                        maxWidth: "300px",
                                      }}
                                    >
                                      <EventCard
                                        event={event}
                                        image={event.image}
                                        index={i}
                                        showDescription={false}
                                      />
                                    </motion.div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>

                      <motion.div
                        className="row justify-content-center align-items-center mt-5"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                      >
                        <div className="col-auto">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <motion.div
                              className="custom-border d-flex align-items-center justify-content-center rounded-circle"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ArrowLeft
                                onClick={handleEventsPrev}
                                ref={prevRef}
                                className="cursor-pointer fs-4 heading"
                              />
                            </motion.div>

                            <motion.div
                              className="custom-border d-flex align-items-center justify-content-center rounded-circle"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ArrowRight
                                onClick={handleEventsNext}
                                ref={nextRef}
                                className="cursor-pointer fs-4 heading"
                              />
                            </motion.div>
                          </div>
                        </div>
                      </motion.div>
                    </>
                  )}

                  {sortedUpcomingEvents.length === 0 && (
                    <div className="row justify-content-center align-items-center">
                      <div className="col-12 text-center py-5">
                        <p className="text-muted mb-0">
                          We are preparing our upcoming spiritual events for
                          you. Please stay tuned for our next gathering.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Visual Journey */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="visual-journey-container text-center my-5 w-100 bg-white">
              <motion.h4
                className="text-uppercase heading mb-4 fw-bold"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              >
                {visualJourneyContent.subtitle}
              </motion.h4>
              <motion.h2
                className="fw-bold mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
              >
                {visualJourneyContent.title}
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
              >
                <CustomCarousel
                  items={images}
                  speed={carouselSettings.carouselSpeed}
                />
              </motion.div>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Home;
